import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBabies } from '@/hooks/useBabies';
import { useBabiesStatsCache } from '@/hooks/useBabiesStatsCache';
import { BabyProfile as BabyProfileType, convertBabyToProfile } from '@/types/baby';
import { useAuth } from '@/contexts/AuthContext';

export const useBabySelection = () => {
  const [selectedBaby, setSelectedBaby] = useState<BabyProfileType | null>(null);
  const selectedBabyIdRef = useRef<string | null>(null);
  const { selectedBaby: globalSelectedBaby, setSelectedBaby: setGlobalSelectedBaby } = useAuth();

  const updateSelectedBaby = useCallback((baby: BabyProfileType | null) => {
    selectedBabyIdRef.current = baby?.id ?? null;
    setSelectedBaby(baby);
    setGlobalSelectedBaby(baby);
  }, [setGlobalSelectedBaby]);
  
  const {
    babies: dbBabies,
    removedBabies,
    isLoading: babiesLoading,
    addBaby,
    removeBaby,
    restoreBaby,
    fetchBabies
  } = useBabies();

  // Get stats for all babies using the cache
  const allBabyIds = useMemo(() => dbBabies.map(b => b.id), [dbBabies]);
  const { getStatsForBaby, isLoading: statsLoading } = useBabiesStatsCache(allBabyIds);

  // Convert database babies to UI baby profiles with actual stats from cache
  const babies = useMemo(() => {
    return dbBabies.map(baby => {
      const stats = getStatsForBaby(baby.id);
      if (stats) {
        return convertBabyToProfile(
          baby,
          stats.triedFoodsCount,
          stats.totalFoodsCount,
          stats.recentTries
        );
      }
      return convertBabyToProfile(baby);
    });
  }, [dbBabies, getStatsForBaby]);

  // Stable string for baby IDs to use as dependency
  const babyIdsKey = useMemo(() => babies.map(b => b.id).join(','), [babies]);

  // Set selected baby from localStorage or first baby - use stable ID comparison
  useEffect(() => {
    if (babies.length === 0) {
      if (selectedBabyIdRef.current !== null) {
        selectedBabyIdRef.current = null;
        setSelectedBaby(null);
      }
      return;
    }

    const savedBabyId = localStorage.getItem('selectedBabyId');
    const globalBabyId = globalSelectedBaby?.id;
    const targetBabyId =
      (globalBabyId && babies.some(baby => baby.id === globalBabyId) ? globalBabyId : null) ||
      (savedBabyId && babies.some(baby => baby.id === savedBabyId) ? savedBabyId : null) ||
      babies[0]?.id;
    
    // Only update if the selected baby ID is different
    if (selectedBabyIdRef.current !== targetBabyId) {
      const targetBaby = babies.find(baby => baby.id === targetBabyId);
      if (targetBaby) {
        selectedBabyIdRef.current = targetBaby.id;
        setSelectedBaby(targetBaby);
        if (globalSelectedBaby?.id !== targetBaby.id) {
          setGlobalSelectedBaby(targetBaby);
        }
      } else if (babies.length > 0) {
        selectedBabyIdRef.current = babies[0].id;
        setSelectedBaby(babies[0]);
        if (globalSelectedBaby?.id !== babies[0].id) {
          setGlobalSelectedBaby(babies[0]);
        }
      }
    } else {
      // Update the baby object if stats have changed but keep same selection
      const currentBaby = babies.find(baby => baby.id === selectedBabyIdRef.current);
      if (currentBaby) {
        setSelectedBaby(prev => {
          if (!prev || prev.triedFoods !== currentBaby.triedFoods || prev.totalFoods !== currentBaby.totalFoods) {
            return currentBaby;
          }
          return prev;
        });
        if (globalSelectedBaby?.id !== currentBaby.id) {
          setGlobalSelectedBaby(currentBaby);
        }
      }
    }
  }, [babyIdsKey, babies, globalSelectedBaby?.id, setGlobalSelectedBaby]);

  // Save selected baby to localStorage whenever it changes
  useEffect(() => {
    if (selectedBaby) {
      localStorage.setItem('selectedBabyId', selectedBaby.id);
    }
  }, [selectedBaby]);

  const handleAddBaby = async (babyData: { name: string; birthDate: string }) => {
    const newBaby = await addBaby({
      name: babyData.name,
      birth_date: babyData.birthDate
    });
    if (newBaby) {
      const babyProfile = convertBabyToProfile(newBaby);
      updateSelectedBaby(babyProfile);
    }
    return newBaby;
  };

  const handleSelectBaby = (baby: BabyProfileType) => {
    updateSelectedBaby(baby);
  };

  const handleRemoveBaby = async (babyId: string) => {
    const success = await removeBaby(babyId);
    if (success && selectedBaby?.id === babyId) {
      const remainingBabies = babies.filter(baby => baby.id !== babyId);
      const newSelectedBaby = remainingBabies.length > 0 ? remainingBabies[0] : null;
      updateSelectedBaby(newSelectedBaby);
    }
    return success;
  };

  const handleRestoreBaby = async (baby: BabyProfileType) => {
    const restored = await restoreBaby(baby.id);
    if (restored) {
      const restoredProfile = convertBabyToProfile(restored);
      updateSelectedBaby(restoredProfile);
    }
    return restored;
  };

  return {
    babies,
    selectedBaby,
    removedBabies: removedBabies.map(baby => convertBabyToProfile(baby)),
    babiesLoading,
    handleAddBaby,
    handleSelectBaby,
    handleRemoveBaby,
    handleRestoreBaby,
    setSelectedBaby: updateSelectedBaby,
    fetchBabies // Add this
  };
};